import { Store, useStore } from '@tanstack/react-store'
import type { CustomDateRange } from '@/lib/date-filter-utils'
import type { TaskStatusEnum } from '@/gen/cortado/v1/settings_pb'

const LOCAL_STORAGE_KEY = 'cortado_tasks_filters'

interface TasksFiltersState {
  selectedStatuses: TaskStatusEnum[]
  selectedAssignees: string[]
  dueDateRange: CustomDateRange | null
  searchQuery: string
}

const defaultState: TasksFiltersState = {
  selectedStatuses: [],
  selectedAssignees: [],
  dueDateRange: null,
  searchQuery: '',
}

function loadFromStorage(): TasksFiltersState {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      // Restore Date objects for dueDateRange
      if (parsed.dueDateRange?.from) {
        parsed.dueDateRange.from = new Date(parsed.dueDateRange.from)
      }
      if (parsed.dueDateRange?.to) {
        parsed.dueDateRange.to = new Date(parsed.dueDateRange.to)
      }
      return { ...defaultState, ...parsed }
    }
  } catch (e) {
    console.warn('Failed to load tasks filters:', e)
  }
  return defaultState
}

function saveToStorage(state: TasksFiltersState) {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state))
  } catch (e) {
    console.warn('Failed to save tasks filters:', e)
  }
}

export const tasksFiltersStore = new Store<TasksFiltersState>(loadFromStorage())

export const tasksFiltersActions = {
  setSelectedStatuses: (statuses: TaskStatusEnum[]) => {
    tasksFiltersStore.setState((state) => {
      const newState = { ...state, selectedStatuses: statuses }
      saveToStorage(newState)
      return newState
    })
  },

  setSelectedAssignees: (assignees: string[]) => {
    tasksFiltersStore.setState((state) => {
      const newState = { ...state, selectedAssignees: assignees }
      saveToStorage(newState)
      return newState
    })
  },

  setDueDateRange: (range: CustomDateRange | null) => {
    tasksFiltersStore.setState((state) => {
      const newState = { ...state, dueDateRange: range }
      saveToStorage(newState)
      return newState
    })
  },

  setSearchQuery: (query: string) => {
    tasksFiltersStore.setState((state) => {
      // Don't persist search query to storage
      return { ...state, searchQuery: query }
    })
  },

  resetFilters: () => {
    tasksFiltersStore.setState(() => {
      const newState = { ...defaultState }
      saveToStorage(newState)
      return newState
    })
  },
}

// Hooks for granular subscriptions
export function useTasksSelectedStatuses() {
  return useStore(tasksFiltersStore, (s) => s.selectedStatuses)
}

export function useTasksSelectedAssignees() {
  return useStore(tasksFiltersStore, (s) => s.selectedAssignees)
}

export function useTasksDueDateRange() {
  return useStore(tasksFiltersStore, (s) => s.dueDateRange)
}

export function useTasksSearchQuery() {
  return useStore(tasksFiltersStore, (s) => s.searchQuery)
}

export function useTasksFilters() {
  return useStore(tasksFiltersStore)
}
